import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";
import {
  endChatSession,
  getHasActiveChatSession,
  getIsMinimized,
  minimizeChatWindow,
  toggleChatWindow,
} from "@b2bportal/core-self-serve";
import type { SupportContext } from "@b2bportal/rbc-hts-assist-api";
import "@hopper-ai/chat-widget/styles.css";
import "@hopper-ai/chat-widget/themes/rbc.css";
import "./hts-assist-chat-widget.css";
import {
  A2uiChatWidgetProvider,
  FloatingChatWidget,
  MessageSurfaces,
  StandaloneSurfaces,
  useA2uiSurfaceEventHandler,
  type ChatWidgetConfig,
} from "@hopper-ai/chat-widget";
import {
  createHandleDeepLinkImplementation,
  htsAssistCatalog,
  HTS_ASSIST_CATALOG_ID,
  HtsAssistLocaleProvider,
} from "@hopper-ai/hts-assist-components";
import type { LiveKitChatProvider } from "@hopper-ai/livekit-chat-provider";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getHtsAssistDeepLinkPath } from "./deepLinks";
import { useHtsAssistAnalytics } from "./htsAssistAnalytics";
import {
  HtsAssistChatStartupError,
  HTS_ASSIST_CHAT_START_TIMEOUT_MS,
} from "./HtsAssistChatStartupError";
import {
  pathnameToEntryPoint,
  toEntryPointSlug,
  type HtsAssistEntryPoint,
} from "./htsAssistEntryPoint";
import { HtsAssistLoadingMessage } from "./HtsAssistLoadingMessage";
import { HtsAssistLobby } from "./HtsAssistLobby";
import { pathnameToSupportContext } from "./htsAssistSupportContext";
import { resolveHtsAssistUserId } from "./htsAssistUserId";
import { useHtsAssistSession } from "./useHtsAssistSession";

import {
  getHtsAssistEntryState,
  openHtsAssistChat,
} from "@/store/htsAssistEntryState";

const HTS_ASSIST_CHAT_DIALOG_SELECTOR =
  ".hts-assist-chat-widget > section[role='dialog']";

export const HtsAssistChatWidget: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { t, language } = useI18nContext();
  const title = t("chat.htsAssistTitle");

  const entryState = useSelector(getHtsAssistEntryState);
  const location = useLocation();
  const routeEntryPointRef = useRef<HtsAssistEntryPoint | undefined>(undefined);
  routeEntryPointRef.current = pathnameToEntryPoint(location.pathname);
  const routeSupportContextRef = useRef<SupportContext | undefined>(undefined);
  routeSupportContextRef.current = pathnameToSupportContext(location.pathname);

  const hasActiveChatSession = useSelector(getHasActiveChatSession);
  const isMinimized = useSelector(getIsMinimized);
  // Keep FloatingChatWidget mounted for the whole active session. Driving
  // isOpen from isMinimized unmounts ChatWidgetProvider (disconnect + wipe
  // transcript). Minimize only hides the panel via CSS.
  const isOpen = hasActiveChatSession;

  const currentUserId = useMemo(() => resolveHtsAssistUserId(), []);

  // Resolve route defaults once per entry/session, so navigation behind the
  // floating widget cannot change the booking used by Start new chat.
  const request = useMemo(() => {
    const entryPoint = entryState.entryPoint ?? routeEntryPointRef.current;
    return {
      conversationMode: "text" as const,
      ...(entryPoint ? { entryPoint: toEntryPointSlug(entryPoint) } : {}),
      supportContext:
        entryState.supportContext ??
        (hasActiveChatSession ? routeSupportContextRef.current : undefined),
      entryAction: entryState.entryAction,
      initialUserQuery: entryState.initialUserQuery,
    };
  }, [entryState, hasActiveChatSession]);
  const {
    chatProvider,
    showInitialLoadingComponent,
    hasError,
    sessionKey,
    csat,
    closeChat,
    retry,
  } = useHtsAssistSession(
    hasActiveChatSession && entryState.experience === "chat",
    request,
    () => dispatch(endChatSession())
  );

  // Changing these implementations recreates the A2UI store and clears cards.
  // Keep them stable across routes while using the latest navigation callback.
  const functionImplementations = useMemo(
    () => [
      createHandleDeepLinkImplementation((deepLink) => {
        const path = getHtsAssistDeepLinkPath(deepLink);

        if (path) {
          navigateRef.current(path);
        } else {
          console.debug("[HtsAssistChatWidget] Unhandled deep link:", deepLink);
        }
      }),
    ],
    []
  );

  const catalogs = useMemo(() => [htsAssistCatalog], []);
  const analytics = useHtsAssistAnalytics({
    sessionKey,
    entryPoint: request.entryPoint,
    chatProvider,
  });

  const startLobbyChat = (initialUserQuery: string) => {
    dispatch(
      openHtsAssistChat({
        entryPoint: entryState.entryPoint,
        initialUserQuery,
      })
    );
  };

  const startBookingChat = (supportContext: SupportContext) => {
    dispatch(
      openHtsAssistChat({
        entryPoint: entryState.entryPoint,
        supportContext,
      })
    );
  };

  const chatWidgetConfig = useMemo<ChatWidgetConfig>(
    () => ({
      theme: "rbc",
      colorMode: "light",
      composerMode: "text",
      trustedLinkDomains: ["rbc-faq.hts.hopper.com"],
      disclaimerText: t("chat.htsAssistDisclaimer"),
      requireInitialGreetingBeforeSend: true,
      // active (0.1.46) replaces the deprecated showLoadingComponent. The
      // localized HtsAssistLoadingMessage stays in place of the built-in
      // organic shimmer until the package ships startupLoading.* strings in
      // fr/fr-CA; the shimmer would render English on the French portal.
      loading: {
        active: showInitialLoadingComponent,
        loadingComponent: <HtsAssistLoadingMessage />,
      },
      messageSurfaceSlot: MessageSurfaces,
      messageSurfacePlacement: "inline",
      standaloneSurfaceSlot: StandaloneSurfaces,
      analytics,
      csat,
      startupTimeout: {
        timeoutMs: HTS_ASSIST_CHAT_START_TIMEOUT_MS,
        renderError: ({ retry }) => (
          <HtsAssistChatStartupError onRetry={retry} />
        ),
      },
    }),
    [t, analytics, showInitialLoadingComponent, csat]
  );

  return (
    <HtsAssistLocaleProvider
      locale={language}
      messageOverrides={{
        "composer.placeholder": t("chat.htsAssistComposerPlaceholder"),
        "timeline.disconnected": t("chat.htsAssistChatEnded"),
      }}
    >
      <A2uiChatWidgetProvider
        key={sessionKey}
        catalogs={catalogs}
        defaultCatalogId={HTS_ASSIST_CATALOG_ID}
        functionImplementations={functionImplementations}
        onAction={(action) => chatProvider?.sendDataAction(action)}
        analytics={analytics}
      >
        <HtsAssistFloatingChatWidgetInner
          provider={chatProvider}
          currentUserId={currentUserId}
          isOpen={isOpen}
          isMinimized={isMinimized}
          onOpen={() => dispatch(toggleChatWindow())}
          onMinimize={() => dispatch(minimizeChatWindow())}
          onClose={() => {
            if (entryState.experience === "lobby") dispatch(endChatSession());
            else void closeChat();
          }}
          title={title}
          config={chatWidgetConfig}
          panelContent={
            hasError ? (
              <HtsAssistChatStartupError onRetry={retry} />
            ) : entryState.experience === "lobby" ? (
              <HtsAssistLobby
                onSelectBooking={startBookingChat}
                onStartQuery={startLobbyChat}
              />
            ) : undefined
          }
        />
      </A2uiChatWidgetProvider>
    </HtsAssistLocaleProvider>
  );
};

interface HtsAssistFloatingChatWidgetInnerProps {
  provider: LiveKitChatProvider | undefined;
  currentUserId: string;
  isOpen: boolean;
  isMinimized: boolean;
  onOpen: () => void;
  onMinimize: () => void;
  onClose: () => void;
  title: string;
  config: ChatWidgetConfig;
  panelContent?: ReactNode;
}

/**
 * useA2uiSurfaceEventHandler must be called from within A2uiChatWidgetProvider,
 * so the FloatingChatWidget render lives in this inner component (same split as
 * whopper's HTSAssistFloatingChatWidgetInner).
 */
const HtsAssistFloatingChatWidgetInner: React.FC<
  HtsAssistFloatingChatWidgetInnerProps
> = ({
  provider,
  currentUserId,
  isOpen,
  isMinimized,
  onOpen,
  onMinimize,
  onClose,
  title,
  config,
  panelContent,
}) => {
  const handleSurfaceEvent = useA2uiSurfaceEventHandler();

  const [sheetMountingContainer, setSheetMountingContainer] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen || typeof window === "undefined") {
      setSheetMountingContainer(null);
      return undefined;
    }
    const animationFrameId = window.requestAnimationFrame(() => {
      const next = document.querySelector<HTMLElement>(
        HTS_ASSIST_CHAT_DIALOG_SELECTOR
      );
      setSheetMountingContainer((current) =>
        current === next ? current : next
      );
    });
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isOpen]);

  const mergedConfig = useMemo<ChatWidgetConfig>(
    () => ({ ...config, sheetMountingContainer }),
    [config, sheetMountingContainer]
  );

  const className = [
    "hts-assist-chat-widget",
    "normalize-exempt",
    isMinimized ? "hts-assist-chat-widget--minimized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <FloatingChatWidget
      provider={provider}
      currentUserId={currentUserId}
      isOpen={isOpen}
      onOpen={onOpen}
      onMinimize={onMinimize}
      onClose={onClose}
      showLauncher={false}
      title={title}
      className={className}
      onSurfaceEvent={handleSurfaceEvent}
      config={mergedConfig}
    >
      {panelContent}
    </FloatingChatWidget>
  );
};
